import { Checkbox, Flex, FlexProps, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useResources } from 'datx-jsonapi-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WATCHLIST_NAME } from '@/components/ui/features/lecture/BookmarkLectureModal/constants';
import { IToastType } from '@/enums/ToastType';
import { useStores } from '@/hooks/useStores';
import { useToast } from '@/hooks/useToast';
import { queryLecturePlaylists } from '@/queries/lecture-playlist';
import { queryProfilePlaylists } from '@/queries/profile-playlists';
import { sessionQueryOptions } from '@/queries/upgrade/sessions';
import { LecturePlaylist } from '@/resources/LecturePlaylist';
import { ProfilePlaylist } from '@/resources/ProfilePlaylist';
import { ThinProfile } from '@/resources/ThinProfile';

interface IPlaylistItemProps extends FlexProps {
  lectureId: string;
}

export const WatchlistItem: FC<IPlaylistItemProps> = ({ lectureId, ...rest }) => {
  const { t } = useTranslation();
  const { store } = useStores();
  const toast = useToast();
  const { data: session } = useQuery(sessionQueryOptions());
  const main_profile = session?.main_profile;
  const { data: lecturePlaylists, mutate: lecturesPlaylistMutate } = useResources(
    queryLecturePlaylists(main_profile.id, { lecture_id: lectureId })
  );
  const { data: profilePlaylists, mutate: mutateProfilePlaylists } = useResources(
    queryProfilePlaylists(main_profile.id)
  );
  const [stateChangeText, setStateChangeText] = useState(null);

  async function createWatchlistIfNotExists() {
    try {
      const watchlistProfilePlaylist = profilePlaylists.find((pp) => pp.name === WATCHLIST_NAME);

      if (watchlistProfilePlaylist) return watchlistProfilePlaylist;

      const response = await store.request(`${ThinProfile.type}/${main_profile.id}/playlists`, 'POST', {
        data: {
          type: ProfilePlaylist.type,
          attributes: {
            name: WATCHLIST_NAME,
          },
        },
      });

      await mutateProfilePlaylists();
      return response.data as ProfilePlaylist;
    } catch (response) {
      toast({ status: IToastType.ERROR, title: response.error[0].detail });
    }
  }

  async function handleLecturePlaylistStateChange(checked: boolean) {
    try {
      const watchlistPlaylist = await createWatchlistIfNotExists();

      if (checked) handleAddLectureToPlaylist(watchlistPlaylist.id);
      if (!checked) handleRemoveLectureFromPlaylist(watchlistPlaylist.id);
    } catch (response) {
      toast({ status: IToastType.ERROR, title: response?.error?.[0].detail });
    }
  }

  async function handleAddLectureToPlaylist(playlistId: string) {
    try {
      await store.request(LecturePlaylist.type, 'POST', {
        data: {
          type: LecturePlaylist.type,
          attributes: {
            lecture_id: lectureId,
            playlist_id: playlistId,
          },
        },
      });

      setStateChangeText(t('newLecture.bookmarkModal.added'));
      lecturesPlaylistMutate();
    } catch (response) {
      toast({ status: IToastType.ERROR, title: response?.error?.[0]?.detail });
    }
  }

  async function handleRemoveLectureFromPlaylist(playlistId) {
    try {
      const lecturePlaylist = lecturePlaylists.find((lp) => lp.playlistId === playlistId);

      await store.request(`${LecturePlaylist.type}/${lecturePlaylist.id}`, 'DELETE', {
        data: {
          type: LecturePlaylist.type,
          attributes: {
            lecture_id: lectureId,
            playlist_id: playlistId,
          },
        },
      });

      setStateChangeText(t('newLecture.bookmarkModal.removed'));
      lecturesPlaylistMutate();
    } catch (response) {
      toast({ status: IToastType.ERROR, title: response?.error?.[0]?.detail });
    }
  }

  function isDefaultChecked() {
    const watchlistProfilePlaylist = profilePlaylists.find((pp) => pp.name === WATCHLIST_NAME);

    return Boolean(lecturePlaylists?.find((lp) => lp.playlistId === watchlistProfilePlaylist?.id));
  }

  return (
    <Flex mt={4} justifyContent="space-between" alignItems="center" {...rest}>
      <Checkbox
        pb="2px"
        defaultChecked={isDefaultChecked()}
        onChange={(e) => handleLecturePlaylistStateChange(e.target.checked)}
      >
        {t('newLecture.bookmarkModal.watchlistCheckbox')}
      </Checkbox>
      <Text textTransform="uppercase" pl="4" flex="1" fontSize="sm" fontWeight="semibold">
        {stateChangeText}
      </Text>
    </Flex>
  );
};
